import { useState, useEffect, useCallback } from 'react';

export interface Enquiry {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  message: string;
  source: string;
  status: 'new' | 'read' | 'responded';
  createdAt: number;
  notes?: string;
}

const DB_NAME = 'BeyondElectricalEnquiries';
const DB_VERSION = 1;
const STORE_NAME = 'enquiries';
const SEEDED_KEY = 'enquiries_seeded_v2';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function addEnquiry(enquiry: Enquiry): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).add(enquiry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getAllEnquiries(): Promise<Enquiry[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).getAll();
    request.onsuccess = () => {
      const results: Enquiry[] = request.result || [];
      results.sort((a, b) => b.createdAt - a.createdAt);
      resolve(results);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function updateEnquiryStatus(id: string, status: Enquiry['status'], notes?: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result as Enquiry;
      if (record) {
        record.status = status;
        if (notes !== undefined) record.notes = notes;
        store.put(record);
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteEnquiry(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function seedPriorEnquiries(): Promise<void> {
  if (localStorage.getItem(SEEDED_KEY)) return;

  const { priorEnquiries } = await import('../mocks/enquiries');
  const db = await openDB();

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    // Remove any old fake prior-* records from previous seeding
    const getAllReq = store.getAll();
    getAllReq.onsuccess = () => {
      const existing: Enquiry[] = getAllReq.result || [];
      existing.forEach((record) => {
        if (record.id.startsWith('prior-')) {
          store.delete(record.id);
        }
      });
      // Add real enquiries (skip if ID already exists)
      priorEnquiries.forEach((e) => {
        const checkReq = store.get(e.id);
        checkReq.onsuccess = () => {
          if (!checkReq.result) store.add(e);
        };
      });
    };

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });

  localStorage.setItem(SEEDED_KEY, 'true');
}

export function useEnquiries() {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await seedPriorEnquiries();
      const data = await getAllEnquiries();
      setEnquiries(data);
    } catch (err) {
      console.error('Failed to load enquiries', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { enquiries, loading, refresh };
}
